import { useRef, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'

import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { MediaModalProps } from 'modules/marketplace_modules/components/Revamp/types'

const CAROUSEL_IMAGE_WIDTH = 800

const optimizeImageUrl = (url: string): string => {
  if (!url) return url
  try {
    const parsed = new URL(url)
    if (parsed.hostname.endsWith('.datocms-assets.com') || parsed.hostname.endsWith('.imgix.net')) {
      parsed.searchParams.set('w', String(CAROUSEL_IMAGE_WIDTH))
      parsed.searchParams.set('auto', 'format')
      return parsed.toString()
    }
  } catch {
    // Not a valid URL, return as-is
  }
  return url
}

const isVideoUrl = (url: string): boolean => {
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv', '.wmv', '.flv', '.m4v']
  const lowerUrl = url.toLowerCase()
  return videoExtensions.some((ext) => lowerUrl.includes(ext))
}

const isImageUrl = (url: string): boolean => {
  const imageExtensions = [
    '.jpg',
    '.jpeg',
    '.png',
    '.gif',
    '.webp',
    '.svg',
    '.bmp',
    '.ico',
    '.tiff',
  ]
  const lowerUrl = url.toLowerCase()
  return imageExtensions.some((ext) => lowerUrl.includes(ext))
}

const isYouTubeUrl = (url: string): boolean => {
  return url.includes('youtube.com') || url.includes('youtu.be')
}

const getYouTubeEmbedUrl = (url: string, autoPlay = false): string => {
  let videoId: string | undefined

  if (url.includes('youtu.be/')) {
    videoId = url.split('youtu.be/')[1]?.split('?')[0]
  } else if (url.includes('youtube.com')) {
    videoId = url.split('v=')[1]?.split('&')[0]
  }

  if (!videoId) {
    return url
  }

  const baseUrl = `https://www.youtube.com/embed/${videoId}`

  if (autoPlay) {
    return `${baseUrl}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=1`
  }

  return baseUrl
}

const Media = ({ video, thumbnail, title, autoPlay, moduleSlug }: MediaModalProps) => {
  const [_isHovered, setIsHovered] = useState(false)
  const [userInteracted, setUserInteracted] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)

  const handleMouseEnter = () => {
    setIsHovered(true)
    videoRef.current?.play()
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
    videoRef.current?.pause()
  }

  const handleVideoPlay = () => {
    if (userInteracted) {
      track(trackingEvents.marketplaceModules.interactsWithVideo, {
        source: 'marketplace_carousel_video',
        module: moduleSlug,
        action: 'play',
      })
    }
  }

  const handleVideoPause = () => {
    setUserInteracted(true)
    track(trackingEvents.marketplaceModules.interactsWithVideo, {
      source: 'marketplace_carousel_video',
      module: moduleSlug,
      action: 'pause',
    })
  }

  const handleVideoClick = () => {
    setUserInteracted(true)
    track(trackingEvents.marketplaceModules.interactsWithVideo, {
      source: 'marketplace_carousel_video',
      module: moduleSlug,
      action: 'click',
    })
  }

  const rawMediaUrl = video?.url || thumbnail.src
  const mediaUrl =
    rawMediaUrl && isImageUrl(rawMediaUrl) ? optimizeImageUrl(rawMediaUrl) : rawMediaUrl || ''
  const shouldRenderVideo = rawMediaUrl && isVideoUrl(rawMediaUrl)
  const shouldRenderYouTube = rawMediaUrl && isYouTubeUrl(rawMediaUrl)
  const shouldRenderImage = rawMediaUrl && isImageUrl(rawMediaUrl)

  return (
    <Box
      borderRadius={{
        all: 'abs016',
      }}
      border={{ all: { width: 's1', color: 'grey300', style: 'solid' } }}
    >
      {shouldRenderVideo ? (
        <video
          ref={videoRef}
          src={mediaUrl}
          title={title || ''}
          loop
          muted
          onMouseOver={!autoPlay ? handleMouseEnter : undefined}
          onMouseOut={!autoPlay ? handleMouseLeave : undefined}
          onPlay={handleVideoPlay}
          onPause={handleVideoPause}
          onSeeked={handleVideoClick}
          style={{ borderRadius: '12px', width: '100%', height: 'auto' }}
          autoPlay={autoPlay}
          controls
        />
      ) : shouldRenderYouTube ? (
        <iframe
          src={getYouTubeEmbedUrl(mediaUrl, autoPlay)}
          title={title || 'YouTube video'}
          allowFullScreen
          allow="autoplay; encrypted-media"
          style={{
            borderRadius: '12px',
            width: '100%',
            aspectRatio: '16/9',
            border: 'none',
          }}
          onClick={handleVideoClick}
        />
      ) : shouldRenderImage ? (
        <img
          src={mediaUrl}
          alt={title || 'Media content'}
          style={{ borderRadius: '12px', width: '100%', height: 'auto' }}
        />
      ) : (
        <Box
          borderRadius={{ all: 'abs012' }}
          background="grey100"
          alignItems="center"
          justifyContent="center"
          style={{ width: '100%', height: '200px' }}
        >
          <Text color="grey600" size="300">
            No media available
          </Text>
        </Box>
      )}
    </Box>
  )
}

export default Media
